import { configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import { api } from '../services/api';
import { cmsApi } from '../services/cmsApi';
import authReducer from '../features/auth/authSlice';
import brandKitReducer from '../features/brand-kit/brandKitSlice';
import templatesReducer from '../features/templates/templatesSlice';
import widgetTempReducer from '../redux/slices/WidgetTempSlice';
import { rtkQueryErrorLogger } from './middleware/errorMiddleware';
import { syncCookiesToLocalStorage, syncAuthStateToCookies } from '../utils/authCookieHelper';

// Sync tokens from cookies to localStorage before loading state
syncCookiesToLocalStorage();

// Load initial state from local storage
const loadState = () => {
  try {
    const serializedState = localStorage.getItem('figma-make-store-v1');
    if (serializedState === null) {
      return undefined;
    }
    const state = JSON.parse(serializedState);

    // Remove obsolete navigation state if needed to prevent Redux warnings
    if (state.navigation) {
      delete state.navigation;
    }

    return state;
  } catch (err) {
    return undefined;
  }
};

// Save state to local storage
const saveState = (state: any) => {
  try {
    const serializedState = JSON.stringify({
      auth: state.auth,
      templates: state.templates,
      // brandKit is large, maybe skip if needed, but let's try
    });
    localStorage.setItem('figma-make-store-v1', serializedState);
  } catch (err) {
    // Ignore write errors
  }
};



export const store = configureStore({
  reducer: {
    [api.reducerPath]: api.reducer,
    [cmsApi.reducerPath]: cmsApi.reducer,
    auth: authReducer,
    brandKit: brandKitReducer,
    templates: templatesReducer,
    widgets: widgetTempReducer,
  } as any,
  preloadedState: loadState(),
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat(
      api.middleware, 
      cmsApi.middleware,
      rtkQueryErrorLogger
    ) as any,
});

// Subscribe to store changes to save state
store.subscribe(() => {
  const state = store.getState();
  saveState(state);
  syncAuthStateToCookies(state.auth);
});

setupListeners(store.dispatch);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

